import type { ToastType } from '@/stores/toast-store';
import { AdminApiError } from './api-client';

interface ToastPayload {
  type: ToastType;
  title: string;
  message?: string;
}

/**
 * fetch の TypeError をネットワークエラーとして判定する。
 *
 * ブラウザの fetch API はネットワーク障害時に TypeError をスローする。
 * メッセージはブラウザにより異なる（"Failed to fetch", "NetworkError" 等）。
 */
function isNetworkError(error: unknown): boolean {
  if (!(error instanceof TypeError)) return false;
  const msg = error.message.toLowerCase();
  return msg.includes('failed to fetch') || msg.includes('networkerror');
}

/**
 * エラーオブジェクトからトースト通知用ペイロードを生成する。
 *
 * AdminApiError のステータスコードに基づき、ユーザー向けの適切な
 * メッセージを決定する。design.md のエラーカテゴリに準拠。
 */
function getErrorToastPayload(error: unknown): ToastPayload {
  // ネットワークエラー（fetch TypeError）
  if (isNetworkError(error)) {
    return {
      type: 'error',
      title: 'ネットワークエラー',
      message: 'ネットワーク接続を確認してください。',
    };
  }

  // AdminApiError: ステータスコード別ハンドリング
  if (error instanceof AdminApiError) {
    switch (error.status) {
      case 401:
        return {
          type: 'error',
          title: 'セッションが期限切れです',
          message: '再ログインしてください。',
        };
      case 403:
        return {
          type: 'error',
          title: '権限エラー',
          message: 'この操作を実行する権限がありません。',
        };
      case 404:
        return {
          type: 'error',
          title: 'リソースが見つかりません',
          message: '要求されたリソースは存在しないか、削除されています。',
        };
      case 409:
        return {
          type: 'error',
          title: '競合エラー',
          message: '既に存在するリソースです。名前を変更してください。',
        };
      case 429:
        return {
          type: 'warning',
          title: 'リクエスト制限',
          message: 'リクエスト制限に達しました。しばらくお待ちください。',
        };
      default:
        // 4xx: バックエンドのメッセージをそのまま表示
        if (error.status >= 400 && error.status < 500) {
          return {
            type: 'error',
            title: error.message,
          };
        }
        // 5xx: サーバーエラー
        return {
          type: 'error',
          title: 'サーバーエラー',
          message: 'サーバーエラーが発生しました。しばらくしてから再度お試しください。',
        };
    }
  }

  // その他の未知のエラー
  return {
    type: 'error',
    title: 'エラーが発生しました',
    message: error instanceof Error ? error.message : undefined,
  };
}

export type { ToastPayload };
export { getErrorToastPayload, isNetworkError };
